import { useCallback } from 'react';

import {
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	encodePromptToAdf,
	encodeSkillPromptToAdf,
	PROMPT_PLACEHOLDER_CHAR,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
type SkillMetadataMap = Record<
	string,
	{ name: string; description: string; [key: string]: unknown }
>;

export type PlaceholderType = 'person' | 'link' | 'skill' | 'generic';

type PlaceholderOptions = {
	shouldFocus?: boolean;
	selectionStartIndex?: number;
	selectionEndIndex?: number;
	insertAtCursor?: boolean;
	insertAtEnd?: boolean;
};

type PlaceholderData = {
	/** Correlates a history insertion without storing the selected title in analytics. */
	historyAcceptanceId?: string;
	thirdPartyStarter?: { source: string; promptId: string };
	prompt: string;
	adfDocument?: DocNode;
	type: PlaceholderType;
	selectionStartIndex: number;
	selectionEndIndex: number;
	shouldFocus?: boolean;
	// Places placeholder type at the cursor position.
	// But should only be used as a last resort, if the placeholder is not inserted at the cursor position by default.
	insertAtCursor?: boolean;
	insertAtEnd?: boolean;
};

type State = {
	placeholder: PlaceholderData | null;
};

const state = createStore({
	initialState: {
		placeholder: null,
	} as State,
	actions: {
		setPlaceholder:
			(placeholder: State['placeholder']): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ placeholder });
			},
	},
});

// exported for testing
export const usePlaceholderStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			setPlaceholder: (
				placeholder: State['placeholder'],
			) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(state);

const getPlaceholderType = (placeholderLabel: string): PlaceholderType => {
	switch (placeholderLabel) {
		case 'person':
			return 'person';
		case 'skill':
			return 'skill';
		case 'status':
		case 'priority':
			return 'generic';
		default:
			return 'link';
	}
};

const isPromptWithPlaceholder = (prompt: string): boolean => {
	return prompt.includes(PROMPT_PLACEHOLDER_CHAR);
};

export const useEditorPlaceholder = (): {
	insertPlaceholder: (
		prompt: string,
		overrideType?: PlaceholderType,
		skillMetadata?: SkillMetadataMap,
		thirdPartyStarter?: PlaceholderData['thirdPartyStarter'],
	) => void;
	insertPlaceholderWithAdf: (
		prompt: string,
		adfDocument: DocNode,
		options?: PlaceholderOptions,
	) => void;
	insertInlineCard: (url: string) => void;
	clearPlaceholder: () => void;
	placeholder: PlaceholderData | null;
	isPromptWithPlaceholder: (prompt: string) => boolean;
} => {
	const [{ placeholder }, { setPlaceholder }] = usePlaceholderStore();

	const insertPlaceholder = useCallback(
		(
			prompt: string,
			overrideType?: PlaceholderType,
			skillMetadata?: SkillMetadataMap,
			thirdPartyStarter?: PlaceholderData['thirdPartyStarter'],
		): void => {
			// TODO: Used for pubsub listener events to convert skill tags to ADF. Remove this once we have a better solution.
			if (overrideType === 'skill') {
				const { adf, promptLength } = encodeSkillPromptToAdf(prompt);
				const offset = FeatureGates.getExperimentValue(
					'rovo_chat_sticky_skill_cursor_fix',
					'isEnabled',
					false,
				)
					? 1
					: 0;
				setPlaceholder({
					prompt,
					adfDocument: adf,
					type: 'generic',
					selectionStartIndex: promptLength + offset,
					selectionEndIndex: promptLength + offset,
				});
				return;
			}

			const { adf, promptLength, placeholderRange } = encodePromptToAdf(
				prompt,
				skillMetadata,
				true,
			);

			const type: PlaceholderType = placeholderRange
				? (overrideType ?? getPlaceholderType(placeholderRange.label))
				: 'generic';

			setPlaceholder({
				prompt,
				adfDocument: adf,
				type,
				...(thirdPartyStarter ? { thirdPartyStarter } : {}),
				selectionStartIndex: placeholderRange?.start ?? promptLength + 1,
				selectionEndIndex: placeholderRange?.end ?? promptLength + 1,
			});
		},
		[setPlaceholder],
	);

	const insertInlineCard = useCallback(
		(url: string): void => {
			const adfDocument = {
				version: 1,
				type: 'doc',
				content: [
					{
						type: 'paragraph',
						content: [
							{ type: 'inlineCard', attrs: { url } },
							{ type: 'text', text: ' ' },
						],
					},
				],
			} as DocNode;

			setPlaceholder({
				prompt: url,
				adfDocument,
				type: 'generic',
				selectionStartIndex: 3,
				selectionEndIndex: 3,
			});
		},
		[setPlaceholder],
	);

	/**
	 * Inserts a placeholder with a pre-built ADF document.
	 * Places cursor at start of content (position 2) to avoid position calculation issues.
	 * Use this when you have content with inline cards or other complex ADF nodes.
	 */
	const insertPlaceholderWithAdf = useCallback(
		(prompt: string, adfDocument: DocNode, options?: PlaceholderOptions): void => {
			setPlaceholder({
				prompt,
				adfDocument,
				type: 'generic',
				// Position 2 is always valid for the default empty paragraph case:
				// doc(0) > paragraph(1) > content start(2).
				selectionStartIndex: options?.selectionStartIndex ?? 2,
				selectionEndIndex: options?.selectionEndIndex ?? options?.selectionStartIndex ?? 2,
				shouldFocus: options?.shouldFocus,
				insertAtCursor: options?.insertAtCursor,
				insertAtEnd: options?.insertAtEnd,
			});
		},
		[setPlaceholder],
	);

	const clearPlaceholder = useCallback((): void => {
		setPlaceholder(null);
	}, [setPlaceholder]);

	return {
		insertPlaceholder,
		insertPlaceholderWithAdf,
		insertInlineCard,
		clearPlaceholder,
		placeholder,
		isPromptWithPlaceholder,
	};
};
